<script setup lang="ts">
import {Icon} from '@iconify/vue'
import {onMounted, onUnmounted, ref} from 'vue'

import type {BellowsDirection} from '@/components/harmonika'
import {BassLayoutEditor, formatNoteForDisplay, InstrumentBody, RAW_DATA} from '@/components/harmonika'

import ControlsBar from './ControlsBar.vue'
import LegendBar from './LegendBar.vue'
import SettingsModal from './SettingsModal.vue'
import {useHarmonikaAudio} from './useHarmonikaAudio'
import {useKeyMappings} from './useKeyMappings'

const showSettings = ref(false)
// Unter-Tabs des Harmonika-Tabs. „Spielen" ist der Simulator, „Bass-Belegung" der Editor der
// Begleitseite – die Tastatur-Handler unten greifen nur im Simulator (siehe `handleKeyDown`).
const view = ref<'play' | 'bass'>('play')

const {
  keyMappings,
  layoutMap,
  updateMapping,
  getKeyLabel,
  findButtonByKeyCode,
  resetToDefaults,
} = useKeyMappings()

const {
  direction,
  activeButtons,
  activeMidiNotes,
  activateButton,
  deactivateButton,
} = useHarmonikaAudio()

function getButtonLabel(rowIdx: number, btnIdx: number) {
  const buttonData = RAW_DATA[rowIdx]?.buttons[btnIdx]
  if (!buttonData) return ''

  const notes = direction.value === 'DRUCK' ? buttonData.druck : buttonData.zug
  return formatNoteForDisplay(notes[0] ?? '')
}

function setDirection(nextDirection: BellowsDirection) {
  direction.value = nextDirection
}

function handleKeyDown(event: KeyboardEvent) {
  if (event.repeat) return
  if (view.value !== 'play') return

  if (event.code === 'Space') {
    event.preventDefault()
    setDirection('ZUG')
    return
  }

  if (showSettings.value) return

  const buttonPosition = findButtonByKeyCode(event.code)
  if (!buttonPosition) return

  event.preventDefault()
  const [rowIdx, btnIdx] = buttonPosition
  activateButton(rowIdx, btnIdx)
}

function handleKeyUp(event: KeyboardEvent) {
  if (view.value !== 'play') return
  if (event.code === 'Space') {
    event.preventDefault()
    setDirection('DRUCK')
    return
  }

  const buttonPosition = findButtonByKeyCode(event.code)
  if (!buttonPosition) return

  event.preventDefault()
  const [rowIdx, btnIdx] = buttonPosition
  deactivateButton(rowIdx, btnIdx)
}

onMounted(() => {
  window.addEventListener('keydown', handleKeyDown)
  window.addEventListener('keyup', handleKeyUp)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown)
  window.removeEventListener('keyup', handleKeyUp)
})
</script>

<template>
  <div class="flex min-h-[70dvh] flex-1 flex-col items-center-safe justify-center-safe overflow-x-auto rounded-2xl bg-stone-900 p-4 font-sans text-stone-800 select-none">
    <SettingsModal
      v-if="showSettings"
      :key-mappings="keyMappings"
      :layout-map="layoutMap"
      @mapping-change="updateMapping"
      @reset-to-defaults="resetToDefaults"
      @close="showSettings = false"
    />

    <div class="flex w-full max-w-6xl flex-col items-center gap-6">
      <!-- Titel-Banner (früher separate HeaderBar der Standalone-App) -->
      <header class="flex w-full items-end justify-between px-4">
        <div class="text-left">
          <h1 class="font-serif text-3xl font-bold tracking-wider text-amber-500 md:text-4xl">
            Steirische
          </h1>
          <p class="text-xs text-stone-400 md:text-sm">
            Web Audio Edition
          </p>
        </div>

        <button
          type="button"
          class="flex items-center gap-2 rounded-lg border border-stone-700 bg-stone-800 px-4 py-2 text-sm text-stone-100 transition-colors hover:bg-stone-700"
          @click="showSettings = true"
        >
          <Icon icon="lucide:settings" class="text-lg"/>
          <span class="hidden sm:inline">Tasten-Layout</span>
        </button>
      </header>

      <nav class="flex w-full gap-1 px-4" aria-label="Harmonika-Ansicht">
        <button
          v-for="tab in [{id: 'play', label: 'Spielen'}, {id: 'bass', label: 'Bass-Belegung'}]"
          :key="tab.id"
          type="button"
          class="rounded-t-lg px-4 py-2 text-sm transition-colors"
          :class="view === tab.id
            ? 'bg-stone-100 font-semibold text-stone-900'
            : 'text-stone-400 hover:bg-stone-800 hover:text-stone-100'"
          @click="view = tab.id as 'play' | 'bass'"
        >
          {{ tab.label }}
        </button>
      </nav>

      <template v-if="view === 'play'">
        <ControlsBar v-model:direction="direction" />

        <InstrumentBody
          variant="simulator"
          :active-buttons="activeButtons"
          :active-midi-notes="activeMidiNotes"
          :get-label="getButtonLabel"
          :get-key-label="getKeyLabel"
          @activate="activateButton"
          @deactivate="deactivateButton"
        />

        <LegendBar :active-buttons-count="activeButtons.size" />
      </template>

      <section v-else class="w-full rounded-xl bg-stone-100 p-4 text-stone-800">
        <BassLayoutEditor />
      </section>
    </div>
  </div>
</template>
